/**
 * SubscribeToBeVisibleBanner
 *
 * Persistent dashboard banner shown when a coach has no active
 * subscription (tier 0). Implements Pattern F from the Subscription
 * Gating Spec — "Inline upgrade hints, never modal nags." This is the
 * core gate awareness: an unsubscribed coach can do everything except
 * be discovered, booked, or paid. The banner is the periphery reminder
 * that the subscription unlocks visibility.
 *
 * Visibility rules:
 *   - Hidden if currentUser is missing (not signed in / loading)
 *   - Hidden if subscriptionDetails.subscription >= 1 (any paid tier)
 *   - Hidden if the user is a participant only (no provider role) —
 *     the gate is for coach-side discoverability
 *   - Hidden on the pricing page itself to avoid duplicate CTA
 *
 * Visual language:
 *   - Subtle, ribbon-style strip across the top of the dashboard
 *   - Forest-green gradient with gold accent dot
 *   - Single CTA button "Choose a plan"
 *   - Not dismissable — this is the canonical gate notice, not a nag.
 *     Dismissing would defeat the purpose. The banner disappears the
 *     moment the coach picks any paid plan.
 *
 * Mounted in MainDashboardWrapper next to SubscriptionMigrationBanner.
 * Both are mutually exclusive in practice — migration banner targets
 * legacy paid users, this one targets unsubscribed users.
 */

import React from 'react';
import { withRouter } from 'react-router-dom';

const SubscribeToBeVisibleBanner = ({ currentUser, history, location }) => {
  if (!currentUser || !currentUser.attributes) return null;

  // Only show to coaches (providers). Participant-only users don't
  // need a marketplace listing, so this CTA would be noise to them.
  const publicData =
    (currentUser.attributes.profile && currentUser.attributes.profile.publicData) || {};
  const isProvider = !!publicData.isProvider;
  if (!isProvider) return null;

  // Hide if any paid tier is active.
  const subscriptionDetails =
    (currentUser.attributes.profile && currentUser.attributes.profile.metadata && currentUser.attributes.profile.metadata.subscriptionDetails) ||
    null;
  const tier =
    subscriptionDetails && typeof subscriptionDetails.subscription === 'number'
      ? subscriptionDetails.subscription
      : 0;
  if (tier >= 1) return null;

  // Hide on the pricing page itself — they're already where the CTA
  // would take them.
  const path = location && location.pathname;
  if (path && path.indexOf('/account/payments') === 0) return null;

  const handleCTA = () => {
    if (history) history.push('/account/payments/pricing');
  };

  return (
    <div
      role="region"
      aria-label="Subscribe to be visible to clients"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        padding: '12px 18px',
        margin: '0 0 12px 0',
        borderRadius: '12px',
        background:
          'linear-gradient(90deg, #064E3B 0%, #0F3F4A 55%, #1E1040 100%)',
        border: '1px solid rgba(251, 191, 36, 0.35)',
        color: '#FFFFFF',
        boxShadow: '0 10px 28px rgba(15, 23, 42, 0.25)',
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flex: '1 1 auto',
          minWidth: '240px',
        }}
      >
        {/* Gold accent dot — mirrors the Viveka "dot" brand element */}
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: '#FBBF24',
            boxShadow: '0 0 0 4px rgba(251, 191, 36, 0.18)',
            flexShrink: 0,
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <strong
            style={{
              fontSize: '15px',
              fontWeight: 700,
              lineHeight: 1.3,
              color: '#FFFFFF',
            }}
          >
            Subscribe to be visible to clients
          </strong>
          <span
            style={{
              fontSize: '13px',
              color: 'rgba(255, 255, 255, 0.78)',
              lineHeight: 1.45,
              marginTop: '2px',
            }}
          >
            Your profile is saved, but your listings stay hidden until
            you choose a plan. 7-day free trial on monthly plans.
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleCTA}
        style={{
          appearance: 'none',
          cursor: 'pointer',
          padding: '10px 18px',
          borderRadius: '10px',
          border: 'none',
          background: '#FBBF24',
          color: '#0F172A',
          fontWeight: 700,
          fontSize: '14px',
          letterSpacing: '0.2px',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Choose a plan
      </button>
    </div>
  );
};

export default withRouter(SubscribeToBeVisibleBanner);
